// Domain enums — the single source of truth for the string-union values the
// Postgres CHECK constraints in ./schema.ts enforce. Both apps/web and
// apps/api import these instead of redeclaring the literals; the const arrays
// double as runtime validators (write payloads) and as the basis for the
// derived union types. Keep the members in lock-step with the CHECK
// constraints in ./schema.ts.

// WHERE ONE DOG IS, on one booking — `booking_dogs.area` (dogs-in-areas AC1).
// Three real places, and the only vocabulary a dog is ever stored with.
//
// `house` is no longer the legacy category it was: it is a bookable area with
// its own capacity line and its own chip (AC5). It had been quietly alive all
// along — 33 bookings ever, one in 2026 — and offered by no control.
export const DOG_AREAS = ["big", "little", "house"] as const;
export type DogArea = (typeof DOG_AREAS)[number];

export const DOG_AREA_LABELS: Record<DogArea, string> = {
  big: "Big",
  little: "Little",
  house: "House",
};

// bookings.area — `bookings_area_check`. WHAT THE BOOKING READS, which is a
// different question to where a dog is, and carries one extra value to say so.
//
// `mix` is retired for `other` (AC4). The rename is not cosmetic. `mix` was
// chosen by hand and meant "these dogs are not all in the same place" — except
// 24 of the 146 mix bookings had exactly ONE dog, so it actually meant
// "somebody was unsure". `other` is DERIVED on every save and never chosen, so
// it cannot mean that: it means the dogs genuinely disagree.
export const AREAS = ["big", "little", "house", "other"] as const;
export type KennelArea = (typeof AREAS)[number];

// Human labels for each area — single source for pills, filters, and pickers.
export const AREA_LABELS: Record<KennelArea, string> = {
  big: "Big",
  little: "Little",
  house: "House",
  other: "Other",
};

// AC4 — the whole of the derivation, in one place because apps/api writes it
// and apps/web previews it, and two copies of this rule would disagree the
// first time somebody changed one.
//
// NEVER NULL, and that is deliberate: 39 files read `bookings.area` and not one
// of them expects an absent value (AC9). A booking nobody has placed reads
// `other` — "we cannot say where this is" — which is true, and which every
// existing reader already handles.
export function deriveBookingArea(dogAreas: readonly (string | null)[]): KennelArea {
  const placed = dogAreas.filter((a): a is string => Boolean(a));
  // An unplaced dog is a disagreement, not an abstention. Ignoring it would let
  // one dog in Big claim a whole booking whose other dog nobody has assigned.
  if (placed.length !== dogAreas.length || placed.length === 0) return "other";
  const distinct = new Set(placed);
  if (distinct.size !== 1) return "other";
  const only = [...distinct][0] as KennelArea;
  return (AREAS as readonly string[]).includes(only) ? only : "other";
}

// dogs.size — the INPUT vocabulary: what a NEW dog may be given. The DB CHECK
// (`dogs_size_check`) is wider — see DOG_SIZES_STORED below — but the create/
// edit validation (apps/api dogs routes) only accepts big or little, so every
// dog added through the app is one of these two.
export const SIZES = ["big", "little"] as const;
export type DogSize = (typeof SIZES)[number];

// The lines capacity is counted against — `capacity_blocks.line`, and the four
// pairs in PORTAL_SETTING_KEYS. NOT a place a dog can be: `kennel` is every dog
// wherever it sleeps, which is why the column is `line` and not `area`.
//
// CAPACITY COUNTS SIZE, NOT AREA — Freddi, 2026-09-11: "the only thing we want
// to differentiate at the moment when calculating capacity is big vs small",
// and "the area config is just for ourselves to know where a dog is staying but
// should not be considered against calculation of big vs small".
//
// This REVERSES the rule dogs-in-areas shipped that morning, and the reversal is
// the product decision rather than a correction of a bug. What a customer can
// book against is how many big and little dogs fit; WHERE a dog sleeps is the
// desk's own record and moves no customer-facing number. `booking_dogs.area`
// stays, and is read by nothing here.
//
// `house` is therefore NOT a line. It is an area, and areas do not gate
// availability.
//
// A dog whose size is not big or little counts on the kennel line and against
// no size line. The vocabulary a NEW dog may be given is big or little alone
// (SIZES), so this only bites on legacy rows — and those heal the next time the
// dog is booked, because the modal now refuses to save a booking carrying one.
export const CAPACITY_LINES = ["kennel", "big", "little"] as const;
export type CapacityLine = (typeof CAPACITY_LINES)[number];

// handover_days.rule — what a dated row does to a day's handovers
// (handover-days). The two states the customer calendar already draws; morning
// only is cut until a date needs it (docs/decision/handover-days-one-rule-per-row.md).
export const HANDOVER_DAY_RULES = ["pm_only", "closed"] as const;
export type HandoverDayRule = (typeof HANDOVER_DAY_RULES)[number];
export const HANDOVER_DAY_RULE_LABEL: Record<HandoverDayRule, string> = { pm_only: "Afternoon only", closed: "Closed" };

// dogs.size — the STORED vocabulary: everything `dogs_size_check` actually
// allows. 'mix'/'house'/'unknown' are legacy values carried in by the sheet
// import (a never-booked dog imports as 'unknown'); they are read and rendered
// but never offered on a new dog — that's SIZES' job. Reads (e.g. a dog
// embedded in a booking or an owner) must expect THIS union, not SIZES.
export const DOG_SIZES_STORED = ["big", "little", "mix", "house", "unknown"] as const;
export type DogSizeStored = (typeof DOG_SIZES_STORED)[number];

// drop_off / collection / shifts.kind — am or pm.
export const PERIODS = ["am", "pm"] as const;
export type Period = (typeof PERIODS)[number];

// A rostered shift slot is a 3-hour block (AM 08:00–11:00, PM 15:30–18:30).
// Single source for the wage maths — the web timesheet (staff/stats) and the
// API finances aggregator both read it instead of hardcoding 3.
export const SLOT_HOURS = 3;

// bookings.status — `bookings_status_check`. `unconfirmed` is the create-default
// for a deposit-less booking; a deposit promotes it to `confirmed` (status-from-
// deposit). It leads the cycle so the lifecycle reads unconfirmed → confirmed →
// arrived → collected → cancelled.
//
// `requested` (migration 0022, portal-foundations) is APPENDED rather than put
// where it belongs in time — a portal request precedes every other state. The
// desk derives STATUS_ORDER from this array and drives its click-to-cycle
// status pill off it, so inserting the new word at the front would change what
// every status control on the desk does today, for a value nothing can yet
// select: the portal that writes it does not exist. Re-ordering the lifecycle
// is `desk-requested-status`' job, on the day the word becomes reachable.
export const STATUSES = [
  "unconfirmed",
  "confirmed",
  "arrived",
  "collected",
  "cancelled",
  "requested",
  // `declined` (migration 0038, accept-or-decline) — a request Patricia said
  // no to. Not a cancelled stay: nothing was ever held and nothing was owed,
  // and the desk's lists tell the two apart. Appended like `requested`, and
  // never offered by any status menu — the only way in is Decline.
  "declined",
] as const;
export type BookingStatus = (typeof STATUSES)[number];

// CLOSED: a booking that is not, and will not be, a stay — nothing held,
// nothing owed. `cancelled` (a stay that was called off) and `declined` (a
// request Patricia said no to). Every "is this a real stay" check on the client
// and in the hand-written API predicates reads THIS list, because adding
// `declined` to STATUSES made a dozen `!== 'cancelled'` checks silently count a
// declined request as a stay in the kennel (PR #251 review, 2026-10-10).
// `countsAsStaySql` in dates.ts is the SQL side of the same rule.
export const CLOSED_STATUSES = ["cancelled", "declined"] as const;
export function isClosedStatus(status: string): boolean {
  return (CLOSED_STATUSES as readonly string[]).includes(status);
}

// WHAT THE GENERIC WRITE MAY NOT TOUCH (PR #251 review). A request is decided,
// not edited — its fields are the customer's until Accept or Decline — and a
// declined one is final: no undo in v1 (Freddi, 2026-10-10), so no status move
// off it by the back door either. Cancelled is closed but NOT locked; the desk
// reinstates a cancelled stay. The modal shuts the form on the same pair.
export const LOCKED_STATUSES = ["requested", "declined"] as const;
export function isLockedStatus(status: string): boolean {
  return (LOCKED_STATUSES as readonly string[]).includes(status);
}
/** The SQL side of the same rule, for a write's own WHERE. */
export function lockedStatusSql(col = "status"): string {
  return `${col} NOT IN (${LOCKED_STATUSES.map((s) => `'${s}'`).join(", ")})`;
}

// WHAT THE GENERIC WRITES MAY SET. `declined` is reached by Decline alone, so
// POST and PATCH /api/bookings refuse it: a status that can only be entered by
// the guarded endpoint cannot be entered by a stale client either.
export const WRITABLE_STATUSES = STATUSES.filter((s) => s !== "declined") as readonly Exclude<BookingStatus, "declined">[];

// bookings.source — `bookings_source_check`, migration 0022. WHERE the booking
// was made, and the fact behind a stay that "began at Requested". NOT NULL
// DEFAULT 'desk', which backfills every pre-existing row correctly in the same
// statement: every booking that existed before the portal was made at the desk.
// No UI flag — this is provenance the desk reads, not a badge it draws.
export const BOOKING_SOURCES = ["desk", "portal"] as const;
export type BookingSource = (typeof BOOKING_SOURCES)[number];

// bookings.payment_status — `bookings_payment_status_check`. DERIVED, never
// asserted: since the payments ledger landed this is recomputed server-side from
// SUM(payments.amount) vs bookings.total on every payment write (see
// apps/api/src/payments/ledger.ts). It stays a stored column because the list
// filters and sorts on it; it is no longer something a client can set.
export const PAYMENT_STATUSES = ["unpaid", "partial", "paid"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

// payments.source — `payments_source_check`. WHERE a payment row came from, and
// the reason the column is not decoration:
//   import — backfilled from the spreadsheet's "Paid" column at migration 0017.
//            received_on is the stay's CHECK-OUT date, an ESTIMATE (the sheet
//            never recorded when money arrived), so the Finances page can mark
//            estimated history apart from money recorded since.
//   modal  — entered by hand in the booking modal, with a real received date.
//   desk   — the Daily desk's "Paid" tap: settles the outstanding balance, dated
//            today. Un-tapping deletes the newest `desk` row for that booking,
//            which is what makes the toggle reversible — hence the column.
//   portal — the customer paid through the app and uploaded a screenshot; a
//            portal row always has a matched proof beside it (migration 0023).
//            Nothing writes it yet — `deposit-read-and-booked` is the first.
export const PAYMENT_SOURCES = ["import", "modal", "desk", "portal"] as const;
export type PaymentSource = (typeof PAYMENT_SOURCES)[number];

// payments.kind — `payments_kind_check`. WHICH FIGURE the row belongs to, which
// is a different question from `source` above (WHERE it came from):
//   deposit   — money booked against the stay: the opening deposit, anything
//               typed into the modal's "Deposit paid" field, and every imported
//               row.
//   remainder — the balance collected at the end: the Daily desk's Paid tap, and
//               anything typed into the modal's "Remainder paid" field.
//
// The split is deliberately NOT inferred from `source`. Doing that meant the
// modal writing `source = 'desk'`, and the settle-undo's key-less fallback
// ("delete the newest desk row" — reached on any page reload, since the desk
// holds its settle keys in a useRef) could then delete a hand-typed remainder,
// destroying a recorded payment. `source` stays provenance so that fallback can
// only ever reach rows the settle endpoint itself wrote.
export const PAYMENT_KINDS = ["deposit", "remainder"] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

// Allowlist roles — the sign-in access tiers held in Cloudflare KV (not a
// Postgres CHECK; the allowlist is the source of truth). `admin` gates the
// account-management API; owner/helper are non-admin internal users; `staff`
// is the most restricted — denied every management surface, it reaches only
// its own self-view (GET /api/me/shifts). Single-sourced here so the API's
// auth stack and the web role gates share one union. NOT staff.role — that
// column is a free-text job title ("Carer").
export const ROLES = ["admin", "owner", "helper", "staff"] as const;
export type Role = (typeof ROLES)[number];

// The allowlist's FULL role set — ROLES plus the portal's `customer`. Two sets
// rather than one widened set, deliberately (portal-foundations AC6):
//
//   ROLES is the DESK's vocabulary. It is what the Account page's role picker
//   offers and what account/routes.ts validates a role edit against. Widening
//   it would let an admin mint a customer from a dropdown — and a customer
//   account is minted by burning an invite token against a household, never by
//   typing an email into the staff list.
//
//   ALLOWLIST_ROLES is what the KV entry may HOLD and what resolveRole may
//   return. A customer therefore signs in, resolves a role, and is then refused
//   by every existing gate, because no requireRoles() call on the desk names
//   `customer`. That refusal is by construction rather than by a new check.
export const ALLOWLIST_ROLES = [...ROLES, "customer"] as const;
export type AllowlistRole = (typeof ALLOWLIST_ROLES)[number];

// holidays.kind — `holidays_kind_check`. The leave categories a staff leave
// entry can carry. Matches apps/api holidays/query.ts validation one-for-one.
export const LEAVE_KINDS = ["annual", "sick", "unpaid"] as const;
export type LeaveKind = (typeof LEAVE_KINDS)[number];

// viewings.status — `viewings_status_check`. Its OWN vocabulary, sharing nothing
// with STATUSES above, and that separation is the point rather than an accident.
// An earlier draft had a viewing carry the booking status `unconfirmed`, which
// would have counted every viewing twice on the Daily attention rail — once on
// the viewings axis and once as a stay awaiting a deposit — while the panel
// beneath listed it once. Three words of its own dissolve the collision by
// construction. Do not "simplify" viewings back onto booking statuses.
//
// `expected` is the only non-terminal state; both others are settled outcomes.
// CONVERSION IS NOT HERE: whether a viewing produced a customer is
// `viewings.booking_id`, a separate field. Status answers "did the visit
// happen"; the link answers "did it produce a customer". Two orthogonal facts.
export const VIEWING_STATUSES = ["expected", "visited", "no_show"] as const;
export type ViewingStatus = (typeof VIEWING_STATUSES)[number];

// viewings.slot — `viewings_slot_check`. Morning or afternoon, which is the
// precision a telephone call actually yields. Storing 09:30 would invent a
// certainty nobody gave us.
//
// Deliberately NOT PERIODS ('am'/'pm'): that vocabulary belongs to the roster
// and to a booking's drop-off/collection, and a shared union would let a shift
// value flow into a viewing unremarked.
export const VIEWING_SLOTS = ["morning", "afternoon"] as const;
export type ViewingSlot = (typeof VIEWING_SLOTS)[number];

// Human labels — single source for the pill, the form's control, and the desk
// row's meta line.
export const VIEWING_STATUS_LABELS: Record<ViewingStatus, string> = {
  expected: "Expected",
  visited: "Visited",
  no_show: "No-show",
};

// AM / PM — the words the booking form already uses for drop-off and
// collection, so a viewing's time reads like every other time in the app
// (Sir, 2026-09-02). The stored vocabulary is unchanged.
export const VIEWING_SLOT_LABELS: Record<ViewingSlot, string> = {
  morning: "AM",
  afternoon: "PM",
};

// The two kinds a mixed list row can be (viewings-in-the-bookings-list). The
// Bookings page's Type filter is a set over these; empty means All.
export const LIST_KINDS = ["booking", "viewing"] as const;
export type ListKind = (typeof LIST_KINDS)[number];


// --- the customer portal (portal-foundations) ------------------------------

// proofs.kind — `proofs_kind_check`, migration 0026. What the uploaded image is
// evidence OF. `deposit` and `remainder` mirror PAYMENT_KINDS because a payment
// proof is evidence for a payment row of that kind; `card` is a vaccination
// certificate and belongs to a dog rather than to money.
export const PROOF_KINDS = ["deposit", "remainder", "card"] as const;
export type ProofKind = (typeof PROOF_KINDS)[number];

// proofs.uploaded_role — `proofs_uploaded_role_check`. WHO put the file there.
// Not the allowlist role: the desk's own drop-zone upload is `desk` whether an
// admin, an owner or a helper did it, because what the column answers is "did
// this come from the household or from us".
export const PROOF_ROLES = ["customer", "desk"] as const;
export type ProofRole = (typeof PROOF_ROLES)[number];

// The upload ceiling and the types that may be stored (§9, AC7). 12 MB clears a
// real phone photograph — a HEIC is 2–4 MB and a 48MP JPEG about 8 — and
// refuses what is not one. The number is here rather than in the route so the
// API's guard and any client-side hint read the same figure.
export const PROOF_MAX_BYTES = 12 * 1024 * 1024;
export const PROOF_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "application/pdf",
] as const;
export type ProofMimeType = (typeof PROOF_MIME_TYPES)[number];

// Settings › Customer portal (R-SET-1, AC11). These are ROWS in the existing
// `settings` table, not columns — the seed lives in migration 0027 and the
// generic PATCH route validates a key against this set rather than a hardcoded
// list, which is what stops the twentieth key needing a twentieth route.
//
// Every key carries a default below. A key with no seed is a key some read path
// invents a number for later, at the moment it is first missed — which is
// exactly how `capacity_big` and `capacity_little` reached a fourth review
// round with no value in them.
export const PORTAL_SETTING_KEYS = [
  // Capacity — the standing lines. THREE LINES, TWO NUMBERS EACH.
  // BIG VS SMALL IS THE ONLY DIFFERENTIATION (Freddi, 2026-09-11). `house` is
  // an area, not a size, and gates nothing a customer books.
  //
  //   soft — amber. "Limited space available"; the customer is warned and may
  //          still book. Patricia confirms it herself.
  //   hard — red. The customer cannot submit.
  //
  // Below soft is green, between them amber, at hard red — the prototype's own
  // words for the kennel pair it drew as "Limited from 50 / Full at 65".
  //
  // The size lines may sum to LESS than the kennel line (20 + 40 against 65
  // does). That is the seeded state and it is not a misconfiguration: the kennel
  // line simply never binds, and the size limits are the only thing that
  // refuses a stay. Patricia raises either past 65 to put it back in charge.
  //
  // A soft ABOVE its own hard is a different matter and is refused — it deletes
  // the amber band silently, and is a typo every time.
  "capacity_kennel_soft",
  "capacity_kennel_hard",
  "capacity_big_soft",
  "capacity_big_hard",
  "capacity_little_soft",
  "capacity_little_hard",
  // Handover — when the gate is open. Two windows a day, and Sunday mornings
  // closed as the standing weekly rule.
  "handover_am",
  "handover_pm",
  "handover_closed_weekly",
  "handover_closed_days",
  // The Christmas period and its nightly charge (Freddi, 2026-10-08: "€5 extra
  // per night … from 15th Dec to 6th January"). The window is two month-days,
  // `MM-DD`, both ends in; a window that crosses New Year is read as one. The
  // charge is per dog-household night, on top of the rate.
  "christmas_from",
  "christmas_to",
  "christmas_surcharge",
  // Money and the bank details the customer is shown on the deposit step.
  "deposit_minimum",
  "bank_account_name",
  "bank_iban",
  // How the customer reaches Patricia, and where the kennel is.
  "whatsapp_number",
  "eircode",
  // How long a proof image is kept after the stay. The row stays; the image goes.
  "proof_retention_days",
  // The five pre-written lines the booking modal offers, filled from the
  // booking: {name}, {dog}, {dogs}, {dates}, {amount}, {reference}, {reason}.
  "msg_registration_link",
  "msg_deposit_chase",
  "msg_card_chase",
  "msg_failed_screenshot",
  "msg_sign_off",
] as const;
export type PortalSettingKey = (typeof PORTAL_SETTING_KEYS)[number];

// The seed values, applied by 0027 with ON CONFLICT DO NOTHING so re-running
// the migration never overwrites a number Patricia has since changed. Stored as
// text because `settings.value` is text; each reader parses its own.
export const PORTAL_SETTING_DEFAULTS: Record<PortalSettingKey, string> = {
  // The kennel pair is the prototype's, verbatim: "Limited from 50 / Full at 65".
  capacity_kennel_soft: "50",
  capacity_kennel_hard: "65",
  // The per-size HARD numbers are Freddi's (2026-09-09). The per-size SOFT ones
  // are a fill: the prototype predates per-size capacity and gives none, so
  // these carry the kennel pair's own ~77% ratio. Seed values Patricia edits,
  // and named in the story as the one number nobody has given.
  capacity_big_soft: "15",
  capacity_big_hard: "20",
  capacity_little_soft: "30",
  capacity_little_hard: "40",
  handover_am: "09:00-10:00",
  handover_pm: "17:00-18:00",
  // JSON, `<day>:<period>`. Sunday mornings only — the one standing rule.
  handover_closed_weekly: '["sun:am"]',
  // JSON array of ISO dates. Patricia's own list; empty until she fills it.
  handover_closed_days: "[]",
  christmas_from: "12-15",
  christmas_to: "01-06",
  christmas_surcharge: "5",
  deposit_minimum: "100",
  bank_account_name: "Beeches Brook Kennels",
  // Blank rather than a placeholder IBAN: a wrong account number shown to a
  // customer with a copy button beside it is worse than a missing one.
  bank_iban: "",
  whatsapp_number: "",
  eircode: "",
  proof_retention_days: "90",
  // messages-one-shape AC9 (2026-10-14): the lines the desk actually sends, in the one shape.
  msg_registration_link:
    "Here’s the link to sign up to our app, where you can manage the booking:\n{link}",
  msg_deposit_chase:
    "Hi {name}, {dogs} is booked in for {dates} — a {deposit} deposit secures it.\n\nPaying hasn’t changed — a bank transfer, same details as always. Once it’s sent, upload a screenshot of the transfer in the app and I’ll confirm the booking once I’ve checked it.\n\nMany tks, Patricia",
  msg_card_chase:
    "Hi {name}, sorry — I couldn’t accept {dog}’s vaccination card.\n\nCould you upload the new one in the app?\n\nMany tks, Patricia",
  msg_failed_screenshot:
    "Hi {name}, sorry — I couldn’t match your {what} screenshot to a transfer.\n\nCould you check it and send it again in the app?\n\nMany tks, Patricia",
  msg_sign_off: "Many tks, Patricia",
};

export function isPortalSettingKey(key: string): key is PortalSettingKey {
  return (PORTAL_SETTING_KEYS as readonly string[]).includes(key);
}
